import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Lock } from "lucide-react";
import { JerichoEnvironmentLayer } from "./JerichoEnvironmentLayer";
import { JerichoCommandRail } from "./JerichoCommandRail";
import { JerichoBlueprintPanel } from "./JerichoBlueprintPanel";
import { JerichoSurveillanceStack } from "./JerichoSurveillanceStack";
import { JerichoDossierCards } from "./JerichoDossierCards";
import { JerichoTerminalWindow } from "./JerichoTerminalWindow";

/**
 * JerichoBlacksiteShell — cinematic black-site command environment.
 * Composition (desktop):
 *   ┌────────────────── classification bar ─────────────────┐
 *   │ rail │ ▒▒▒  TERMINAL (hero monitor)  ▒▒▒ │ blueprint │
 *   │      │                                   │   stack   │
 *   ├──────┴───── intel ecosystem ribbon ──────┴───────────┤
 *   │              dossier vault (drawers)                  │
 *   └───────────────────────────────────────────────────────┘
 * The environment layer (mountains/perimeter/topo/blueprint) sits as a
 * fixed background behind everything.
 */
export const JerichoBlacksiteShell = ({ drawers }) => {
  return (
    <div
      className="min-h-screen text-[#E4DFD3] relative bg-[#04030a] overflow-x-hidden"
      data-testid="jericho-blacksite-shell"
    >
      {/* Cinematic environment layer */}
      <JerichoEnvironmentLayer />

      {/* Classification strip (top, sticky-feeling) */}
      <div className="relative z-20 border-b border-[#2A2722] bg-black/55 backdrop-blur-[3px]">
        <div className="max-w-[1640px] mx-auto px-3 sm:px-5 py-2 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <span
              className="block w-2 h-2 rounded-full bg-[#D92D20] animate-pulse"
              style={{ boxShadow: "0 0 8px rgba(217,45,32,0.9)" }}
            />
            <span className="font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.4em] text-[#D92D20] whitespace-nowrap">
              // TOP SECRET · OMEGA
            </span>
            <span className="hidden md:inline font-mono text-[9px] uppercase tracking-[0.3em] text-[#5a544a]">
              · BLACK-SITE COMMAND · NODE 01
            </span>
          </div>
          <div className="hidden lg:flex items-center gap-3 font-mono text-[9px] uppercase tracking-[0.3em] text-[#A39E93]">
            <span><span className="text-[#5DD4B3]">●</span> UPLINK SECURE</span>
            <span><span className="text-[#E0A526]">●</span> GRID ROUTED</span>
            <span><span className="text-[#D92D20]">●</span> SURVEILLANCE LIVE</span>
            <span className="text-[#5a544a]">39°48′N · 117°02′W</span>
          </div>
          <Link
            to="/"
            data-testid="jericho-back-home"
            className="shrink-0 inline-flex items-center gap-2 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] hover:text-[#E4DFD3] px-2 sm:px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.3em]"
          >
            <ArrowLeft size={11} />
            <span className="hidden sm:inline">EXFIL · RETURN TO SURFACE</span>
            <span className="sm:hidden">EXFIL</span>
          </Link>
        </div>
      </div>

      {/* HERO ZONE — the command room */}
      <section className="relative z-10 max-w-[1640px] mx-auto px-3 sm:px-5 pt-5 sm:pt-8 pb-6">
        {/* Above-fold label / mission tag */}
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4 sm:mb-5">
          <div className="flex items-center gap-2">
            <div className="h-px w-8 bg-[#D92D20]" />
            <span className="font-mono text-[9px] uppercase tracking-[0.45em] text-[#D92D20]">
              // SITE-J · COMMAND ROOM
            </span>
          </div>
          <span className="hidden md:inline font-mono text-[9px] uppercase tracking-[0.35em] text-[#5a544a]">
            SEC-DEPTH 3 · CONTAINMENT INTACT
          </span>
        </div>

        {/* Main hero composition */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-5 items-start">
          {/* Left rail — floats on the environment, no card BG */}
          <div className="lg:col-span-2 order-2 lg:order-none">
            <JerichoCommandRail />
          </div>

          {/* Center monitor (hero, larger) */}
          <div className="lg:col-span-7 order-1 lg:order-none relative">
            {/* Subtle "platform" glow beneath the monitor */}
            <div
              aria-hidden="true"
              className="absolute -inset-x-6 -bottom-10 h-24 pointer-events-none"
              style={{
                background:
                  "radial-gradient(ellipse at 50% 0%, rgba(93,212,179,0.18) 0%, rgba(93,212,179,0.04) 35%, transparent 70%)",
                filter: "blur(8px)",
              }}
            />
            <JerichoTerminalWindow />
          </div>

          {/* Right intel column: blueprint + status callouts */}
          <div className="lg:col-span-3 order-3 lg:order-none flex flex-col gap-4">
            <JerichoBlueprintPanel />
            <CompactStatusStrip />
          </div>
        </div>
      </section>

      {/* INTEL ECOSYSTEM ribbon */}
      <section className="relative z-10 max-w-[1640px] mx-auto px-3 sm:px-5 pb-6">
        <SectionDivider label="// INTEL · ECOSYSTEM" code="OPS·LATTICE" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-5">
          {/* Long horizontal field log spanning most of the width */}
          <div className="lg:col-span-8">
            <FieldLogPanel />
          </div>
          {/* Compressed surveillance stack */}
          <div className="lg:col-span-4">
            <JerichoSurveillanceStack />
          </div>
        </div>
      </section>

      {/* DOSSIER VAULT */}
      <section className="relative z-10 max-w-[1640px] mx-auto px-3 sm:px-5 pb-8">
        <SectionDivider label="// VAULT · DOSSIERS" code="ARCH·OMEGA" />
        <JerichoDossierCards drawers={drawers} />
      </section>

      {/* FOOTER plate */}
      <section className="relative z-10 max-w-[1640px] mx-auto px-3 sm:px-5 pb-10">
        <div
          className="border border-[#2A2722] bg-black/55 backdrop-blur-[2px] px-3 sm:px-4 py-3 flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3 sm:justify-between"
          style={{ boxShadow: "inset 0 0 24px rgba(0,0,0,0.6)" }}
        >
          <div className="flex items-start gap-2.5 min-w-0">
            <Lock className="text-[#E0A526] shrink-0 mt-0.5" size={14} />
            <p className="text-[11px] text-[#A39E93] leading-relaxed">
              This page is not indexed and not linked from the public site. If you found it,
              you found it on purpose. Keep that to yourself.
            </p>
          </div>
          <div className="flex items-center gap-3 font-mono text-[9px] uppercase tracking-[0.35em] text-[#5a544a] shrink-0">
            <span>// EOF · NODE-01</span>
            <span className="text-[#3a342c]">·</span>
            <span>{new Date().getUTCFullYear()}</span>
            <span className="text-[#3a342c]">·</span>
            <span>DECLASS · NEVER</span>
          </div>
        </div>
      </section>

      {/* Scoped animations */}
      <style>{`
        @keyframes jerichoSweep {
          0%   { transform: translateY(-30%); }
          100% { transform: translateY(130vh); }
        }
        .jericho-sweep {
          animation: jerichoSweep 9s linear infinite;
          will-change: transform;
        }
        @keyframes jerichoRadar {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes jerichoBlink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.25; }
        }
        @media (prefers-reduced-motion: reduce) {
          .jericho-sweep, [style*="jerichoRadar"] { animation: none !important; }
        }
      `}</style>
    </div>
  );
};

// =============================================================================
// Section divider — slim ribbon with code + grow line
// =============================================================================
const SectionDivider = ({ label, code }) => (
  <div className="flex items-center gap-3 mb-4">
    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#D92D20]/40 to-[#D92D20]/60" />
    <div className="flex items-baseline gap-3">
      <span className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#D92D20]">
        {label}
      </span>
      <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#5a544a]">
        / {code}
      </span>
    </div>
    <div className="h-px flex-1 bg-gradient-to-l from-transparent via-[#D92D20]/40 to-[#D92D20]/60" />
  </div>
);

// =============================================================================
// CompactStatusStrip — sits under the blueprint on the right
// =============================================================================
const CompactStatusStrip = () => (
  <div
    className="border border-[#2A2722] bg-black/55 backdrop-blur-[2px]"
    style={{ boxShadow: "inset 0 0 24px rgba(0,0,0,0.6)" }}
  >
    <div className="px-3 py-1.5 border-b border-[#2A2722] flex items-center justify-between">
      <span className="font-mono text-[9px] uppercase tracking-[0.4em] text-[#E0A526]">
        // SITUATION
      </span>
      <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#5a544a]">
        SIT-09
      </span>
    </div>
    <div className="grid grid-cols-3 divide-x divide-[#2A2722]">
      <Stat label="THREAT" value="MOD" tone="warn" />
      <Stat label="ASSETS" value="07" tone="ok" />
      <Stat label="OPSEC" value="GOOD" tone="ok" />
    </div>
    <div className="px-3 py-2 border-t border-[#2A2722] font-mono text-[10px] text-[#A39E93] leading-relaxed">
      <div className="text-[#5a544a] uppercase tracking-[0.3em] text-[8.5px] mb-1">// BRIEF</div>
      <p>
        Operator advised: cross-reference traffic spiked 24h. Vault-Ω
        handshake nominal. Hold containment until next sweep.
      </p>
    </div>
  </div>
);

const Stat = ({ label, value, tone }) => {
  const txt = {
    ok: "text-[#5DD4B3]",
    warn: "text-[#E0A526]",
    alert: "text-[#D92D20]",
    default: "text-[#E4DFD3]",
  }[tone];
  return (
    <div className="px-2 py-1.5">
      <div className="font-mono text-[8.5px] uppercase tracking-[0.3em] text-[#7a7062]">
        {label}
      </div>
      <div className={`font-mono text-[13px] tracking-[0.2em] ${txt}`}>{value}</div>
    </div>
  );
};

// =============================================================================
// FieldLogPanel — horizontal "embedded ops screen" that breaks card-rhythm
// =============================================================================
const FieldLogPanel = () => (
  <div
    className="relative border border-[#2A2722]"
    style={{
      background:
        "linear-gradient(155deg, #0d0c0a 0%, #08080a 60%, #04030a 100%)",
      boxShadow:
        "0 30px 50px -20px rgba(0,0,0,0.85), inset 0 0 30px rgba(0,0,0,0.5)",
    }}
  >
    {/* Top warning stripe */}
    <div
      className="h-[3px] w-full opacity-50"
      style={{
        background:
          "repeating-linear-gradient(45deg, #D92D20 0 6px, #0a0907 6px 12px)",
      }}
    />

    {/* Header */}
    <div className="px-3 py-2 border-b border-[#2A2722] flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span className="block w-1.5 h-1.5 rounded-full bg-[#5DD4B3]" />
        <span className="font-mono text-[9px] uppercase tracking-[0.4em] text-[#5DD4B3]">
          // FIELD LOG · 72H WINDOW
        </span>
      </div>
      <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#5a544a]">
        LOG-04 · CASE-08
      </span>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-12 gap-0">
      {/* Left: log entries */}
      <div className="md:col-span-7 px-4 py-3 md:border-r border-[#2A2722]">
        <ul className="space-y-2 font-mono text-[11px] leading-relaxed text-[#A39E93]">
          <LogEntry stamp="T-08:14" tag="OBSERVED" tone="warn">
            Subject-A reentered grid-3. Cross-ref against drawer-02 returns 2 partial matches.
          </LogEntry>
          <LogEntry stamp="T-06:02" tag="INTERCEPT" tone="ok">
            STN-2 handshake confirmed. Vault-Ω channel nominal. No spillover into public mirror.
          </LogEntry>
          <LogEntry stamp="T-03:21" tag="HANDLER" tone="default">
            Handler advised: hold publication on case-08 until source-3 archive re-validates.
          </LogEntry>
          <LogEntry stamp="T-00:42" tag="ANOMALY" tone="alert">
            Phantom packet, origin masked. Routed through STN-9. Marked for editor review.
          </LogEntry>
        </ul>
      </div>

      {/* Right: numeric readouts */}
      <div className="md:col-span-5 px-4 py-3">
        <div className="grid grid-cols-2 gap-2 mb-3">
          <Readout label="DOSSIERS·LIVE" value="14" tone="default" />
          <Readout label="CANDIDATES" value="47" tone="warn" />
          <Readout label="REVIEW QUEUE" value="09" tone="warn" />
          <Readout label="SEALED" value="03" tone="alert" />
        </div>
        <div className="border-t border-[#2A2722] pt-2">
          <div className="font-mono text-[8.5px] uppercase tracking-[0.3em] text-[#5a544a] mb-1">
            // OPERATOR NOTE
          </div>
          <p className="font-mono text-[10px] text-[#CBB892] italic leading-relaxed">
            &ldquo;Nothing here is sourced to the public archive. Treat all reading as
            pre-decisional. Containment first, truth second.&rdquo;
          </p>
          <div className="mt-2 font-mono text-[9px] uppercase tracking-[0.3em] text-[#7a7062]">
            — handler · 04
          </div>
        </div>
      </div>
    </div>
  </div>
);

const LogEntry = ({ stamp, tag, tone, children }) => {
  const c = {
    default: "text-[#A39E93]",
    ok: "text-[#5DD4B3]",
    warn: "text-[#E0A526]",
    alert: "text-[#D92D20]",
  }[tone];
  return (
    <li className="grid grid-cols-[60px_90px_1fr] gap-2 items-baseline">
      <span className="text-[#5a544a] tabular-nums">{stamp}</span>
      <span className={`uppercase tracking-[0.3em] text-[9px] ${c}`}>{tag}</span>
      <span className="text-[#A39E93]">{children}</span>
    </li>
  );
};

const Readout = ({ label, value, tone }) => {
  const txt = {
    ok: "text-[#5DD4B3]",
    warn: "text-[#E0A526]",
    alert: "text-[#D92D20]",
    default: "text-[#E4DFD3]",
  }[tone];
  return (
    <div className="border border-[#2A2722] px-2 py-1.5 bg-black/40">
      <div className="font-mono text-[8.5px] uppercase tracking-[0.3em] text-[#7a7062]">
        {label}
      </div>
      <div className={`font-mono text-[18px] tracking-[0.18em] ${txt}`}>{value}</div>
    </div>
  );
};

export default JerichoBlacksiteShell;
